/* =====================================================================
 * @auw/app-shell — Workspace mode registry
 *
 * Single source of truth for per-mode presentation metadata. Tenant manifests
 * select which subset is active via `features.modes`; the shell consumes the
 * resolved list via `resolveWorkspaceModes()` (Phase 4c).
 *
 * Ordering invariant: WORKSPACE_ORDER must only contain ids that exist in
 * WORKSPACE_MODES; callers rely on this for stable sidebar rendering.
 * ===================================================================== */

import {
  Bot,
  Radio,
  FileText,
  FileSignature,
  Folder,
  Send,
  Settings2,
  ShieldCheck,
  Puzzle,
  LayoutGrid,
  Newspaper,
  Wrench,
  Wand2,
} from 'lucide-react';
import type { WorkspaceMode } from '@auw/types';
import type { WorkspaceModeInfo } from './types';

export const WORKSPACE_MODES: Record<WorkspaceMode, WorkspaceModeInfo> = {
  agent:        { icon: Bot,             labelKey: 'nav.agent',      letter: 'A', color: '#D4915A', showTopBar: true,  showInput: true,  showDragDrop: true  },
  'live-agent': { icon: Radio,           labelKey: 'nav.liveAgent',  letter: 'L', color: '#E06B50', showTopBar: true,  showInput: true,  showDragDrop: false },
  document:     { icon: FileText,        labelKey: 'nav.document',   letter: 'D', color: '#6B9EC2', showTopBar: true,  showInput: true,  showDragDrop: true  },
  'doc-agent':  { icon: FileSignature,   labelKey: 'nav.docAgent',   letter: 'D', color: '#5A8FB3', showTopBar: false, showInput: false, showDragDrop: false },
  storage:      { icon: Folder,          labelKey: 'nav.storage',    letter: 'R', color: '#8C8C8C', showTopBar: true,  showInput: false, showDragDrop: false },
  message:      { icon: Send,            labelKey: 'nav.message',    letter: 'M', color: '#A8889A', showTopBar: true,  showInput: false, showDragDrop: false },
  widget:       { icon: LayoutGrid,      labelKey: 'nav.widget',     letter: 'W', color: '#8F9DB5', showTopBar: true,  showInput: false, showDragDrop: false },
  feed:         { icon: Newspaper,       labelKey: 'nav.feed',       letter: 'F', color: '#7B8BA6', showTopBar: true,  showInput: false, showDragDrop: false },
  tools:        { icon: Wrench,          labelKey: 'nav.tools',      letter: 'T', color: '#7EA37A', showTopBar: true,  showInput: false, showDragDrop: false },
  'app-manage':  { icon: Settings2,       labelKey: 'nav.appManage',  letter: 'G', color: '#8C8C8C', showTopBar: false, showInput: false, showDragDrop: false },
  'admin-page':  { icon: ShieldCheck,     labelKey: 'nav.adminPage', letter: 'A', color: '#6B7280', showTopBar: false, showInput: false, showDragDrop: false },
  plugin:        { icon: Puzzle,         labelKey: 'nav.plugin',     letter: 'P', color: '#7C6BB3', showTopBar: false, showInput: false, showDragDrop: false },
  'skill-create': { icon: Wand2,         labelKey: 'nav.skillCreate', letter: 'K', color: '#D4915A', showTopBar: false, showInput: false, showDragDrop: false },
};

/**
 * Default sidebar ordering for workspace modes. Excludes management-only
 * modes (storage/app-manage/admin-page/plugin/skill-create) which render in
 * the separate "관리" section or via dedicated routes.
 *
 * Tenants can override this with `tenant.workspace.modeOrder` (Phase 4c).
 */
export const WORKSPACE_ORDER: WorkspaceMode[] = [
  'agent', 'live-agent', 'doc-agent', 'document',
];

/**
 * Set of all known workspace-mode ids, derived from WORKSPACE_MODES at
 * module init. Kept as a Set for O(1) membership checks — `isWorkspaceMode`
 * is called once per entry in tenant-supplied `modeOrder` overrides, so
 * the allocation pays for itself after only a handful of lookups.
 */
const WORKSPACE_MODE_IDS: ReadonlySet<string> = new Set(
  Object.keys(WORKSPACE_MODES),
);

/**
 * Type guard: `true` when `value` is one of the known workspace mode ids.
 *
 * Used by tenant manifest loaders to silently drop unknown ids from
 * `modeOrder` / `defaultMode` without failing the whole manifest — this
 * matches the forgiving policy used elsewhere (e.g. `features` accepts
 * any string key).  Callers that want strict validation should layer
 * their own error reporting on top.
 */
export function isWorkspaceMode(value: unknown): value is WorkspaceMode {
  return typeof value === 'string' && WORKSPACE_MODE_IDS.has(value);
}
